import * as Utils from '@js/modules/utils';
import I18n from '@js/modules/translations';

import Notification from '@js/components/layouts/notification';

import {sendReview} from '@js/actions/reviewActions';

class ReviewManager {
    _userId = parseInt(Utils.getCookie('user.connected_id') || window.currentUserId || 0);

    _reviewPopupElement = 'review-popup';
    _reviewModalElement = 'review-modal';

    _selectedRating = 0;

    _delayBeforePopup = 5000;
    _daysSinceDismissed = 3;

    _minScrollDistance = 500;
    _hasScrolledEnough = false;

    /* Public methods
    ******************** */
    initialize = () => {
        if (this._hasUserReviewed()) {
            return;
        }

        setTimeout(() => {
            this._initScrollListener();
        }, this._delayBeforePopup);
    };

    /* Private methods
    ******************** */
    _initScrollListener = () => {
        const handleScroll = () => {
            const scrollY = window.scrollY || window.pageYOffset;

            if (scrollY >= this._minScrollDistance && !this._hasScrolledEnough) {
                this._hasScrolledEnough = true;
                this._showReviewPopup();
            } else if (scrollY < this._minScrollDistance && this._hasScrolledEnough) {
                this._hasScrolledEnough = false;
                this._hidePopup();
            }
        };

        window.addEventListener('scroll', handleScroll);
    };

    _hasUserReviewed = () => {
        const reviewed = localStorage.getItem('review_submitted');
        const dismissed = localStorage.getItem('review_dismissed');
        const dismissedTime = localStorage.getItem('review_dismissed_time');

        if (reviewed === 'true') {
            return true;
        }

        // Wait for 3 days before showing the popup again
        if (dismissed === 'true' && dismissedTime) {
            const daysSinceDismissed = (Date.now() - parseInt(dismissedTime)) / (1000 * 60 * 60 * 24);
            return daysSinceDismissed < this._daysSinceDismissed;
        }

        return false;
    };

    _showReviewPopup = () => {
        const $reviewPopup = document.getElementById(this._reviewPopupElement);
        if (!$reviewPopup) {
            return;
        }

        $reviewPopup.classList.remove('hidden');

        // Event listeners pour la mini popup
        const $miniPopup = $reviewPopup.querySelector('.review-popup-mini');
        const $closeBtn = $reviewPopup.querySelector('.review-popup-close');

        $miniPopup.addEventListener('click', (event) => {
            if (!event.target.classList.contains('review-popup-close')) {
                this._openModal();
            }
        });

        $closeBtn.addEventListener('click', (event) => {
            event.stopPropagation();
            this._dismissPopup();
        });
    };

    _openModal = () => {
        const $modal = document.getElementById(this._reviewModalElement);
        if (!$modal) {
            return;
        }

        $modal.classList.remove('hidden');
        this._initModalEvents();
    };

    _closeModal = () => {
        const $modal = document.getElementById(this._reviewModalElement);
        if ($modal) {
            $modal.classList.add('hidden');
        }
    };

    _initModalEvents = () => {
        const $modal = document.getElementById(this._reviewModalElement);
        const $overlay = $modal.querySelector('.review-modal-overlay');
        const $closeBtn = $modal.querySelector('.review-modal-close');
        const $cancelBtn = $modal.querySelector('#review-cancel');
        const $form = $modal.querySelector('#review-form');
        const $stars = $modal.querySelectorAll('.star');

        // Fermer la modal
        const closeModal = () => {
            this._closeModal();
        };

        $overlay.addEventListener('click', closeModal);
        $closeBtn.addEventListener('click', closeModal);
        $cancelBtn.addEventListener('click', () => {
            closeModal();
            this._dismissPopup();
        });

        $stars.forEach(($star) => {
            $star.addEventListener('click', () => {
                this._selectedRating = parseInt($star.dataset.rating);
                this._updateStars($stars, this._selectedRating);
            });

            $star.addEventListener('mouseenter', () => {
                const rating = parseInt($star.dataset.rating);
                this._updateStars($stars, rating);
            });
        });

        $modal.querySelector('.review-stars').addEventListener('mouseleave', () => {
            this._updateStars($stars, this._selectedRating);
        });

        $form.addEventListener('submit', (event) => {
            event.preventDefault();

            this._submitReview($form);
        });
    };

    _updateStars = ($stars, rating) => {
        $stars.forEach(($star, index) => {
            if (index < rating) {
                $star.classList.add('active');
            } else {
                $star.classList.remove('active');
            }
        });
    };

    _submitReview = ($form) => {
        if (this._selectedRating === 0) {
            Notification.warning(I18n.t('js.review.note'));
            return;
        }

        const $submitBtn = $form.querySelector('#review-submit');
        $submitBtn.disabled = true;
        $submitBtn.textContent = I18n.t('js.review.sending');

        const formData = new FormData($form);
        const data = {
            user_id: this._userId,
            rating: this._selectedRating,
            comment: formData.get('comment')
        };

        // Add email if user is not connected
        const email = formData.get('email');
        if (email) {
            data.email = email;
        }

        sendReview(data)
            .then(() => {
                localStorage.setItem('review_submitted', 'true');
                Notification.success(I18n.t('js.review.thanks'));
                this._closeModal();
                this._hidePopup();
            })
            .catch(() => {
                Notification.error(I18n.t('js.review.error'));
            })
            .finally(() => {
                $submitBtn.disabled = false;
                $submitBtn.textContent = I18n.t('js.review.send');
            });
    };

    _dismissPopup = () => {
        localStorage.setItem('review_dismissed', 'true');
        localStorage.setItem('review_dismissed_time', Date.now().toString());
        this._hidePopup();
    };

    _hidePopup = () => {
        const $reviewPopup = document.getElementById(this._reviewPopupElement);
        if ($reviewPopup) {
            $reviewPopup.classList.add('hidden');
        }
    };
}

export default new ReviewManager();
